import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import SimpleLayout from '@/layouts/SimpleLayout';
import MaterialIcon from '@/components/MaterialIcon';
import SmartLink from '@/components/SmartLink';
import { useTracking } from '@mygpt/app/utils';

/**
 * 導入事例一覧ページ
 * GBaseSupportSLP の showcase.html を移植
 */

interface CaseItem {
  to: string;
  badge: string;
  company: string;
  title: React.ReactNode;
  desc: string;
  image: string;
  alt: string;
  ariaLabel: string;
  isPriority?: boolean;
}

const CASES: CaseItem[] = [
  {
    to: '/showcase/sapporo-factory',
    badge: '商業施設・大型複合施設',
    company: 'サッポロ不動産開発株式会社様「サッポロファクトリー」',
    title: (
      <>
        Smart Kiosk で<span style={{ color: '#7C3AED' }}>自動回答率 90%+</span> — 約 150 ショップの案内業務を自動化
      </>
    ),
    desc: '日本初のビール工場跡地に立つ大型複合商業施設での Smart Kiosk 導入事例。',
    image: '/showcase/sapporo/sapporo-factory-ai-information-actual.jpg',
    alt: 'サッポロファクトリー館内に設置された「AI INFORMATION」Smart Kiosk — 音声・タッチで 4 言語案内',
    ariaLabel: 'サッポロファクトリー 導入事例：Smart Kiosk で自動回答率 90%+ を実現',
    isPriority: true,
  },
  {
    to: '/showcase/newoman-takanawa',
    badge: '商業施設・フラッグシップ',
    company: '株式会社ルミネ様「NEWoMan 高輪」',
    title: (
      <>
        案内所・電話対応<span style={{ color: '#7C3AED' }}>50%+ 削減</span> — AI と有人コンシェルジュのシームレス案内
      </>
    ),
    desc: 'ルミネ史上最大規模の新施設での多言語インバウンド対応事例。',
    image: 'https://raw.githubusercontent.com/AilisonC147/GBaseSupportSLP/main/banner.jpg',
    alt: 'NEWoMan 高輪 GBase Support 導入事例 — ルミネ史上最大規模の新施設',
    ariaLabel: 'NEWoMan 高輪 導入事例：案内所・電話対応 50%+ 削減を実現',
  },
];

const Showcase: React.FC = () => {
  const { trackContactClick, trackSignupClick } = useTracking();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <SimpleLayout>
      <Helmet>
        <title>GBase Support 導入事例｜商業施設・複合施設の AI コンシェルジュ実績</title>
        <meta
          name="description"
          content="GBase Support の導入事例一覧。NEWoMan 高輪・サッポロファクトリーなど、首都圏から北海道まで商業施設での AI コンシェルジュ導入実績をご紹介。案内所対応の自動化・多言語インバウンド対応・Smart Kiosk 運用の具体事例。"
        />
        <meta
          name="keywords"
          content="GBase Support, 導入事例, AIコンシェルジュ, 商業施設 AI, NEWoMan 高輪, サッポロファクトリー, Smart Kiosk, 多言語 案内, インバウンド対応, リゾーム"
        />
        <meta property="og:image" content="https://cs.gbase.ai/showcase/og-image.jpg" />
        <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />
        <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined" rel="stylesheet" />
      </Helmet>

      <div className="font-solution bg-white leading-relaxed text-solution-text">
        {/* Hero Section */}
        <section
          className="py-10 md:py-14 text-center border-b border-gray-200"
          style={{
            background:
              'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(139, 92, 246, 0.16) 0%, transparent 75%), linear-gradient(180deg, rgba(139, 92, 246, 0.06) 0%, rgba(139, 92, 246, 0.02) 100%)',
          }}
        >
          <div className="max-w-5xl mx-auto px-6">
            <h1 className="text-2xl lg:text-4xl font-extrabold leading-snug tracking-wide mb-3">
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #7C68FB 0%, #A78BFA 100%)' }}
              >
                GBase Support 導入事例
              </span>
              <span className="block mt-2 text-xl lg:text-2xl font-bold text-gray-700">現場で動く AI サポート。</span>
            </h1>
            <p className="text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
              お客様対応から社内ヘルプまで、GBase Support が現場で選ばれています。
              実際の導入企業の取り組みと成果をご紹介します。
            </p>
          </div>
        </section>

        {/* Case Cards */}
        <section id="cases" className="py-20" aria-labelledby="cases-heading">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 id="cases-heading" className="text-3xl font-bold text-gray-900">
                導入企業の声と成果
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {CASES.map((item, i) => (
                <SmartLink
                  key={i}
                  to={item.to}
                  className="block bg-white rounded-2xl overflow-hidden border border-gray-200 transition-all duration-300 hover:-translate-y-1.5 group"
                  style={{ boxShadow: '0 4px 20px rgba(139,92,246,0.06)' }}
                >
                  <div className="overflow-hidden bg-gray-100" style={{ aspectRatio: '16 / 10' }}>
                    <img
                      src={item.image}
                      alt={item.alt}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading={item.isPriority ? 'eager' : 'lazy'}
                      decoding="async"
                    />
                  </div>
                  <div className="p-7">
                    <span
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold mb-4"
                      style={{ backgroundColor: '#EDE9FE', color: '#6D28D9' }}
                    >
                      {item.badge}
                    </span>
                    <p className="text-xs text-gray-500 mb-2">{item.company}</p>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-5">{item.desc}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: '#7C3AED' }}>
                      詳細を見る
                      <MaterialIcon name="arrow_forward" className="!text-[18px]" />
                    </span>
                  </div>
                </SmartLink>
              ))}
            </div>
          </div>
        </section>

        {/* Growth Signal */}
        <section className="py-12 bg-white border-t border-gray-100">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <p className="text-sm text-gray-500 mb-2">― 導入事例は順次公開中 ―</p>
            <p className="text-base text-gray-700 leading-relaxed">
              今後も新しい導入事例を随時公開予定です。
              <br />
              業界別の事例情報や、貴社に近い規模の事例をお求めの方は、
              <SmartLink
                to="/contact"
                className="font-semibold hover:underline"
                style={{ color: '#7C3AED' }}
                onClick={() => trackContactClick('showcase_growth_signal')}
              >
                お問い合わせ
              </SmartLink>
              より個別にご案内いたします。
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section
          className="py-20 px-8 text-center border-t border-gray-200"
          aria-labelledby="cta-heading"
          style={{
            background:
              'radial-gradient(ellipse 55% 60% at 50% 50%, rgba(139, 92, 246, 0.22) 0%, transparent 75%), linear-gradient(180deg, rgba(139, 92, 246, 0.08) 0%, rgba(245, 158, 11, 0.04) 100%)',
          }}
        >
          <h2 id="cta-heading" className="text-2xl md:text-3xl font-extrabold mb-3.5 text-[#1A1A2E]">
            貴社でも、GBase Support の導入を検討しませんか？
          </h2>
          <p className="text-gray-600 mb-7 max-w-3xl mx-auto leading-relaxed">
            業種・規模・運用体制に合わせて、最適な構成と導入ステップをご提案します。
            <br />
            類似事例の共有・実機デモのご相談も承ります。
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <SmartLink
              to="/contact"
              className="inline-flex items-center justify-center px-7 py-3 rounded-full font-semibold text-white text-sm transition-all hover:-translate-y-0.5"
              style={{
                background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)',
              }}
              onClick={() => trackContactClick('showcase_cta')}
            >
              お問い合わせ・ご相談
            </SmartLink>
            <SmartLink
              to="/register"
              className="inline-flex items-center justify-center px-7 py-3 rounded-full font-semibold text-sm transition-all bg-white border-[1.5px] border-gray-300 text-gray-900 hover:border-[#8B5CF6] hover:bg-[rgba(139,92,246,0.05)]"
              onClick={() => trackSignupClick('showcase_cta')}
            >
              14 日間無料トライアル
            </SmartLink>
          </div>
        </section>
      </div>
    </SimpleLayout>
  );
};

export default Showcase;
